import ArgType from 'notebook/tables/excess/ArgType';
import FunctionCategory from 'notebook/tables/excess/FunctionCategory';
import {EvaluablePrimitiveSpreadsheetFunction} from 'notebook/tables/excess/FunctionConstants';
import * as util from 'notebook/tables/excess/Util';
import divide from 'notebook/tables/excess/util/divide';
import filterNumbers from 'notebook/tables/excess/util/filterNumbers';
import {coerceToPrimitive} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

export default {
  name: 'MEDIAN',
  category: FunctionCategory.STATS,
  shortDescription: 'Median (middle) value in a numeric dataset.',
  description:
    'Returns the median (or middle) number in a numeric dataset. If the dataset contains an even number of values, returns the average of the two middle numbers.',
  params: [
    {
      name: 'value',
      type: ArgType.any,
      description: 'The first value or range to consider when calculating the median value.',
      coerce: (typedValue: TypedValue, context?) =>
        coerceToPrimitive(typedValue, {
          shouldCoerceStringToNumber: true,
          shouldCoerceDateToNumber: true,
          languageTag: context?.languageTag,
          timezone: context?.timezone,
        }),
    },
  ],
  repeatableLastParamType: ArgType.any,
  repeatableLastParamDescription:
    'Additional values or ranges to consider when calculating the median value.',

  eval: (...values): number => {
    // filterNumbers handles flattening arrays and filtering out non-numeric values
    const numberValues = filterNumbers(values)
      .filter((num) => !Number.isNaN(num))
      .sort((a, b) => a - b);
    const n = numberValues.length;

    if (n === 0) {
      throw new util.NumericalError('MEDIAN requires at least one numeric value');
    }

    const mid = Math.floor(n / 2);
    if (n % 2 === 1) {
      // Odd number of elements – return the middle value directly.
      return numberValues[mid];
    }

    // Even number of elements – average the two middle values.
    return divide(numberValues[mid - 1] + numberValues[mid], 2, 'MEDIAN');
  },
} satisfies EvaluablePrimitiveSpreadsheetFunction;
